import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  MapPin, X, Loader2, Users, Building2, Plus, Search, Trash2, Upload, Pencil, Map as MapIcon, List as ListIcon, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminTerritoriesMap, deriveStatus, STATUS_LABELS_MAP, STATUS_ORDER } from "./AdminTerritoriesMap";
import { AdminModalOverlay, AdminModalCard, AdminModalBody } from "@/components/AdminModal";

const DERIVED_STATUS_CLS = {
  disponible: "bg-sage/20 text-navy", a_reaffecter: "bg-amber-100 text-amber-700",
  attribue: "bg-cognac/20 text-cognac-dark", non_couvert: "bg-red-100 text-red-600",
};
const HISTORY_LABELS = {
  creation: "Création du territoire", communes_ajoutees: "Communes ajoutées", communes_retirees: "Communes retirées",
  commune_retiree: "Commune retirée", reserver: "Réservé", liberer: "Libéré", suspendre: "Suspendu",
  attribuer: "Attribué", changer_partenaire: "Partenaire changé", renommer: "Territoire modifié",
};
const ROUTING_LABELS = {
  AUTO_ASSIGNED: "Affecté auto", WAITING_PARTNER: "En attente partenaire", ACCEPTED: "Accepté",
  PARTNER_REFUSED: "Refusé", UNCOVERED: "Non couvert", CENTRAL_REVIEW: "À réaffecter",
  PROPOSED_TO_NEIGHBOR: "Proposé à un voisin", NEIGHBOR_ACCEPTED: "Accepté (voisin)", ARCHIVED: "Archivé",
};
const ROUTING_CLS = {
  AUTO_ASSIGNED: "bg-sky-100 text-sky-700", WAITING_PARTNER: "bg-sky-100 text-sky-700", ACCEPTED: "bg-sage/20 text-navy",
  PARTNER_REFUSED: "bg-red-100 text-red-600", UNCOVERED: "bg-orange-100 text-orange-700", CENTRAL_REVIEW: "bg-amber-100 text-amber-700",
  PROPOSED_TO_NEIGHBOR: "bg-indigo-100 text-indigo-700", NEIGHBOR_ACCEPTED: "bg-sage/20 text-navy", ARCHIVED: "bg-slate-100 text-slate-500",
};
const IMPORT_STATUS_LABELS = {
  valide: "Prêt à importer", deja_attribue_ailleurs: "Déjà ailleurs (sera déplacée)",
  deja_present: "Déjà dans ce territoire", doublon_fichier: "Doublon dans le fichier", code_inconnu: "Code INSEE inconnu",
};
const IMPORT_STATUS_CLS = {
  valide: "bg-sage/20 text-navy", deja_attribue_ailleurs: "bg-amber-100 text-amber-700",
  deja_present: "bg-slate-100 text-slate-500", doublon_fichier: "bg-slate-100 text-slate-500", code_inconnu: "bg-red-100 text-red-600",
};
const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const CommuneSearchAdd = ({ onAdd, excludeIds = [], testPrefix }) => {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = term.trim();
    if (query.length < 2) { setResults([]); return; }
    let active = true;
    setLoading(true);
    const params = /^\d{2,5}$/.test(query) ? { cp: query.padEnd(5, "") } : { q: query };
    const timer = setTimeout(() => {
      api.get("/communes/search", { params })
        .then((res) => { if (active) setResults(res.data || []); })
        .catch(() => { if (active) setResults([]); })
        .finally(() => { if (active) setLoading(false); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [term]);

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          data-testid={`${testPrefix}-commune-search`}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Code postal ou nom de commune…"
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-navy focus:border-cognac focus:outline-none"
        />
      </div>
      {loading && <p className="mt-2 text-xs text-slate-400">Recherche…</p>}
      {results.length > 0 && (
        <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-slate-100 p-2">
          {results.filter((r) => !excludeIds.includes(r.insee_code)).map((r) => (
            <button
              key={r.insee_code}
              type="button"
              data-testid={`${testPrefix}-commune-result-${r.insee_code}`}
              onClick={() => { onAdd(r); setTerm(""); setResults([]); }}
              className="flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm text-navy transition-colors hover:bg-cream"
            >
              <span>{r.name} <span className="text-xs text-slate-400">({r.postal_code})</span></span>
              <Plus className="h-3.5 w-3.5 text-cognac" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TerritoryCsvImport = ({ territoryId, onImported }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setLoading(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/admin/territories/${territoryId}/communes/import-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
      const initChecked = {};
      res.data.rows.forEach((r) => { initChecked[r.insee_code] = ["valide", "deja_attribue_ailleurs"].includes(r.status); });
      setChecked(initChecked);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmImport = async () => {
    const codes = preview.rows.filter((r) => checked[r.insee_code]).map((r) => r.insee_code);
    if (codes.length === 0) { toast.error("Sélectionnez au moins une commune à importer"); return; }
    setImporting(true);
    try {
      await api.post(`/admin/territories/${territoryId}/communes`, { insee_codes: codes });
      toast.success(`${codes.length} commune(s) importée(s)`);
      setPreview(null);
      setOpen(false);
      onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  if (!open) {
    return (
      <button data-testid="territory-csv-import-open" onClick={() => setOpen(true)}
        className="mt-2 flex items-center gap-1.5 text-xs font-bold text-cognac transition-colors hover:text-cognac-dark">
        <Upload className="h-3.5 w-3.5" /> Importer un fichier CSV de communes
      </button>
    );
  }

  return (
    <div data-testid="territory-csv-import-panel" className="mt-3 rounded-xl border border-dashed border-slate-300 p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-navy">Import CSV (colonne <code>code_insee</code>)</p>
        <button data-testid="territory-csv-import-close" onClick={() => { setOpen(false); setPreview(null); }} className="text-slate-400 hover:text-navy">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {!preview && (
        <input
          data-testid="territory-csv-import-file"
          ref={fileRef}
          type="file"
          accept=".csv"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="mt-3 w-full text-xs text-slate-500"
        />
      )}
      {loading && <p className="mt-2 text-xs text-slate-400">Analyse du fichier…</p>}
      {preview && (
        <div className="mt-3">
          <p className="text-xs text-slate-500">
            {preview.summary.total} lignes — {preview.summary.valide || 0} prêtes, {preview.summary.deja_attribue_ailleurs || 0} à déplacer,{" "}
            {preview.summary.deja_present || 0} déjà présentes, {preview.summary.doublon_fichier || 0} doublons, {preview.summary.code_inconnu || 0} inconnues.
          </p>
          <div data-testid="territory-csv-import-preview-list" className="mt-2 max-h-48 space-y-1 overflow-y-auto">
            {preview.rows.map((r, i) => (
              <label key={`${r.insee_code}-${i}`} data-testid={`territory-csv-import-row-${r.insee_code}-${i}`}
                className="flex items-center gap-2 rounded-lg border border-slate-100 px-2.5 py-1.5 text-xs">
                <input
                  type="checkbox"
                  disabled={!["valide", "deja_attribue_ailleurs"].includes(r.status)}
                  checked={!!checked[r.insee_code]}
                  onChange={(e) => setChecked((prev) => ({ ...prev, [r.insee_code]: e.target.checked }))}
                />
                <span className="flex-1 text-navy">{r.name || r.insee_code} <span className="text-slate-400">({r.insee_code})</span></span>
                {r.current_territory_code && r.status === "deja_attribue_ailleurs" && (
                  <span className="text-slate-400">depuis {r.current_territory_code}</span>
                )}
                <span className={`rounded-full px-2 py-0.5 font-bold ${IMPORT_STATUS_CLS[r.status]}`}>{IMPORT_STATUS_LABELS[r.status]}</span>
              </label>
            ))}
          </div>
          <button
            data-testid="territory-csv-import-confirm"
            disabled={importing}
            onClick={confirmImport}
            className="mt-3 w-full rounded-full bg-navy px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50"
          >
            {importing ? "Import en cours…" : `Confirmer l'import (${preview.rows.filter((r) => checked[r.insee_code]).length})`}
          </button>
        </div>
      )}
    </div>
  );
};

const TerritoryCommunesPanel = ({ territoryId, testPrefix }) => {
  const [communes, setCommunes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchCommunes = useCallback(() => {
    api.get(`/admin/territories/${territoryId}/communes`)
      .then((res) => setCommunes(res.data.communes))
      .catch(() => toast.error("Impossible de charger les communes"))
      .finally(() => setLoading(false));
  }, [territoryId]);

  useEffect(() => { fetchCommunes(); }, [fetchCommunes]);

  const addCommune = async (c) => {
    setBusy(true);
    try {
      await api.post(`/admin/territories/${territoryId}/communes`, { insee_codes: [c.insee_code] });
      toast.success(`${c.name} ajoutée au territoire`);
      fetchCommunes();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeCommune = async (insee_code) => {
    setBusy(true);
    try {
      await api.delete(`/admin/territories/${territoryId}/communes/${insee_code}`);
      toast.success("Commune retirée");
      fetchCommunes();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testPrefix} className="mt-4">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Communes du territoire ({communes.length})</p>
      <p className="mt-1 text-xs text-slate-400">
        Ajouter une commune la déplace automatiquement depuis son territoire actuel (diviser / fusionner des secteurs).
      </p>
      <div className="mt-2">
        <CommuneSearchAdd onAdd={addCommune} excludeIds={communes.map((c) => c.insee_code)} testPrefix={testPrefix} />
      </div>
      <TerritoryCsvImport territoryId={territoryId} onImported={fetchCommunes} />
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
      ) : (
        <div className="mt-3 max-h-52 space-y-1 overflow-y-auto">
          {communes.map((c) => (
            <div key={c.insee_code} data-testid={`${testPrefix}-commune-row-${c.insee_code}`}
              className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5 text-sm">
              <span className="text-navy">{c.name} <span className="text-xs text-slate-400">({(c.postal_codes || [])[0]})</span></span>
              <button
                type="button"
                disabled={busy}
                data-testid={`${testPrefix}-commune-remove-${c.insee_code}`}
                onClick={() => removeCommune(c.insee_code)}
                className="text-slate-400 transition-colors hover:text-red-500 disabled:opacity-50"
                aria-label={`Retirer ${c.name}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateTerritoryModal = ({ onClose, onCreated }) => {
  const [departments, setDepartments] = useState([]);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [departmentCode, setDepartmentCode] = useState("");
  const [communes, setCommunes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/departments").then((res) => setDepartments(res.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!code.trim() || !name.trim() || !departmentCode) {
      toast.error("Code, nom et département sont requis");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/territories", {
        code: code.trim(), name: name.trim(), department_code: departmentCode,
        commune_insee_codes: communes.map((c) => c.insee_code),
      });
      toast.success("Territoire créé");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      data-testid="territory-create-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy/70 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-7 sm:rounded-3xl sm:p-9"
      >
        <div className="flex items-start justify-between">
          <h2 className="font-display text-2xl font-medium text-navy">Nouveau territoire</h2>
          <button onClick={onClose} data-testid="territory-create-close"
            className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-navy hover:text-white" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Code (ex. 70-LURE)</label>
            <input data-testid="territory-create-code" value={code} onChange={(e) => setCode(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-navy focus:border-cognac focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Nom du territoire</label>
            <input data-testid="territory-create-name" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-navy focus:border-cognac focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Département</label>
            <select data-testid="territory-create-department" value={departmentCode} onChange={(e) => setDepartmentCode(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-navy focus:border-cognac focus:outline-none">
              <option value="">— Sélectionner —</option>
              {departments.map((d) => <option key={d.code} value={d.code}>{d.code} · {d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Communes (optionnel à la création)</label>
            <div className="mt-1.5">
              <CommuneSearchAdd
                testPrefix="territory-create"
                excludeIds={communes.map((c) => c.insee_code)}
                onAdd={(c) => setCommunes((prev) => [...prev, c])}
              />
            </div>
            {communes.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {communes.map((c) => (
                  <span key={c.insee_code} data-testid={`territory-create-commune-chip-${c.insee_code}`}
                    className="flex items-center gap-1 rounded-full bg-cream px-3 py-1 text-xs text-navy">
                    {c.name}
                    <button type="button" onClick={() => setCommunes((prev) => prev.filter((x) => x.insee_code !== c.insee_code))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <button data-testid="territory-create-submit" disabled={busy} onClick={submit}
          className="mt-6 w-full rounded-full bg-navy px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">
          {busy ? "Création…" : "Créer le territoire"}
        </button>
      </motion.div>
    </motion.div>
  );
};

const TerritoryUncoveredProspectsPanel = ({ territoryId, testPrefix }) => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [neighborOptions, setNeighborOptions] = useState({});
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/admin/prospects", { params: { territory_id: territoryId } }).then((res) => {
      const filtered = res.data.filter((p) => ["UNCOVERED", "CENTRAL_REVIEW"].includes(p.routing_status));
      setProspects(filtered);
      filtered.forEach((p) => {
        api.get(`/admin/prospects/${p.id}/neighbor-partners`)
          .then((r) => setNeighborOptions((prev) => ({ ...prev, [p.id]: r.data })))
          .catch(() => {});
      });
    }).finally(() => setLoading(false));
  }, [territoryId]);

  const propose = async (prospectId) => {
    const partnerId = selected[prospectId];
    if (!partnerId) { toast.error("Sélectionnez un partenaire limitrophe"); return; }
    setBusy(prospectId);
    try {
      await api.post(`/admin/prospects/${prospectId}/propose`, { partner_id: partnerId });
      toast.success("Prospect proposé au partenaire limitrophe");
      setProspects((prev) => prev.filter((p) => p.id !== prospectId));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (loading || prospects.length === 0) return null;

  return (
    <div data-testid={testPrefix} className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Zone non couverte — proposer à un partenaire limitrophe</p>
      <div className="mt-3 space-y-2.5">
        {prospects.map((p) => {
          const options = neighborOptions[p.id] || [];
          return (
            <div key={p.id} data-testid={`${testPrefix}-row-${p.id}`} className="rounded-xl bg-white p-3">
              <p className="text-sm font-bold text-navy">
                {p.firstname} {p.lastname} <span className="text-xs font-normal text-slate-400">— {p.city}</span>
              </p>
              {options.length === 0 ? (
                <p className="mt-1.5 text-xs text-slate-400">Aucun partenaire actif dans les territoires limitrophes.</p>
              ) : (
                <div className="mt-2 flex gap-2">
                  <select
                    data-testid={`${testPrefix}-select-${p.id}`}
                    value={selected[p.id] || ""}
                    onChange={(e) => setSelected((prev) => ({ ...prev, [p.id]: e.target.value }))}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-navy focus:border-cognac focus:outline-none"
                  >
                    <option value="">— Choisir un partenaire —</option>
                    {options.map((o) => (
                      <option key={o.partner_id} value={o.partner_id}>{o.partner_name} ({o.territory_code})</option>
                    ))}
                  </select>
                  <button
                    data-testid={`${testPrefix}-propose-${p.id}`}
                    disabled={busy === p.id}
                    onClick={() => propose(p.id)}
                    className="rounded-lg bg-navy px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50"
                  >
                    Proposer
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TerritoryDossiersPanel = ({ territoryId, testPrefix }) => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/prospects", { params: { territory_id: territoryId } })
      .then((res) => setProspects(res.data.slice(0, 8)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [territoryId]);

  if (loading) return null;

  return (
    <div data-testid={testPrefix} className="mt-5">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Derniers dossiers ({prospects.length})</p>
      {prospects.length === 0 ? (
        <p className="mt-2 text-xs text-slate-400">Aucun dossier pour ce territoire.</p>
      ) : (
        <div className="mt-2 max-h-44 space-y-1.5 overflow-y-auto">
          {prospects.map((p) => (
            <div key={p.id} data-testid={`${testPrefix}-row-${p.id}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-1.5 text-xs">
              <span className="font-semibold text-navy">{p.firstname} {p.lastname}</span>
              <span className="text-slate-500">{p.city}</span>
              <span className={`rounded-full px-2 py-0.5 font-bold ${ROUTING_CLS[p.routing_status]}`}>{ROUTING_LABELS[p.routing_status]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TerritoryHistoryPanel = ({ history, partners, testPrefix }) => {
  if (!history || history.length === 0) return null;
  const partnerName = (id) => {
    const p = partners.find((x) => x.id === id);
    return p ? `${p.firstname} ${p.lastname}` : "Partenaire";
  };
  return (
    <div data-testid={testPrefix} className="mt-5">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Historique des attributions</p>
      <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto">
        {[...history].reverse().map((h, i) => (
          <div key={i} data-testid={`${testPrefix}-row-${i}`}
            className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-1.5 text-xs">
            <span className="font-semibold text-navy">{HISTORY_LABELS[h.action] || h.action}</span>
            <span className="flex-1 text-right text-slate-500">
              {h.partner_id ? partnerName(h.partner_id) : ""}
              {Array.isArray(h.detail) && ` (${h.detail.length})`}
              {h.name ? ` → ${h.name}` : ""}
              {h.code ? ` [${h.code}]` : ""}
            </span>
            <span className="whitespace-nowrap text-slate-400">{h.at ? fmtDate(h.at) : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminTerritories = () => {
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("liste");
  const [statusFilter, setStatusFilter] = useState("Tous");
  const [regionFilter, setRegionFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [search, setSearch] = useState("");
  const [communeMatchIds, setCommuneMatchIds] = useState(null);
  const [detail, setDetail] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renameCode, setRenameCode] = useState("");

  const fetchTerritories = useCallback(() => {
    api.get("/admin/territories")
      .then((res) => setTerritories(res.data))
      .catch(() => toast.error("Impossible de charger les territoires"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTerritories(); }, [fetchTerritories]);
  useEffect(() => { api.get("/admin/partners").then((res) => setPartners(res.data)).catch(() => {}); }, []);

  // Recherche par commune ou code postal (réutilise /api/communes/search puis /api/admin/territories?insee_code=)
  useEffect(() => {
    const term = search.trim();
    const isCp = /^\d{5}$/.test(term);
    const isNameLike = /^[a-zA-ZÀ-ÿ\s-]{3,}$/.test(term);
    if (!isCp && !isNameLike) { setCommuneMatchIds(null); return; }
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/communes/search", { params: isCp ? { cp: term } : { q: term } });
        const found = res.data || [];
        if (!active) return;
        if (found.length === 0) { setCommuneMatchIds(null); return; }
        const idLists = await Promise.all(
          found.slice(0, 8).map((c) =>
            api.get("/admin/territories", { params: { insee_code: c.insee_code } }).then((r) => r.data.map((t) => t.id)).catch(() => [])
          )
        );
        if (active) setCommuneMatchIds(new Set(idLists.flat()));
      } catch {
        if (active) setCommuneMatchIds(null);
      }
    }, 350);
    return () => { active = false; clearTimeout(timer); };
  }, [search]);

  const openDetail = (t) => {
    api.get(`/admin/territories/${t.id}`).then((res) => { setDetail(res.data); setSelectedPartner(res.data.partner_id || ""); setRenaming(false); });
  };

  const runAction = async (action) => {
    if ((action === "attribuer" || action === "changer_partenaire") && !selectedPartner) {
      toast.error("Sélectionnez un partenaire");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/territories/${detail.id}`, { action, partner_id: selectedPartner || null });
      toast.success("Territoire mis à jour");
      fetchTerritories();
      openDetail(detail);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const startRename = () => { setRenaming(true); setRenameName(detail.name); setRenameCode(detail.code); };

  const saveRename = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/territories/${detail.id}`, { action: "renommer", name: renameName, code: renameCode });
      toast.success("Territoire modifié");
      setRenaming(false);
      fetchTerritories();
      openDetail(detail);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteTerritory = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/territories/${detail.id}`);
      toast.success("Territoire supprimé");
      setDetail(null);
      fetchTerritories();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const term = search.trim().toLowerCase();
  const filtered = territories.filter((t) => {
    if (statusFilter !== "Tous" && deriveStatus(t) !== statusFilter) return false;
    if (regionFilter && t.region !== regionFilter) return false;
    if (departmentFilter && t.department_code !== departmentFilter) return false;
    if (partnerFilter === "__none__" && t.partner_id) return false;
    if (partnerFilter && partnerFilter !== "__none__" && t.partner_id !== partnerFilter) return false;
    if (!term) return true;
    const partnerText = t.partner ? `${t.partner.firstname} ${t.partner.lastname}` : "";
    const localMatch = `${t.code} ${t.name} ${t.department} ${partnerText}`.toLowerCase().includes(term);
    if (localMatch) return true;
    return communeMatchIds ? communeMatchIds.has(t.id) : false;
  });

  const regionOptions = [...new Set(territories.map((t) => t.region).filter(Boolean))].sort();
  const departmentOptions = Object.values(
    territories.reduce((acc, t) => {
      if (t.department_code) acc[t.department_code] = { code: t.department_code, name: t.department };
      return acc;
    }, {})
  ).sort((a, b) => a.code.localeCompare(b.code));

  const stats = {
    total: territories.length,
    non_couvert: territories.filter((t) => deriveStatus(t) === "non_couvert").length,
    a_reaffecter: territories.filter((t) => deriveStatus(t) === "a_reaffecter").length,
    disponible: territories.filter((t) => deriveStatus(t) === "disponible").length,
    attribue: territories.filter((t) => deriveStatus(t) === "attribue").length,
    partenaires_actifs: partners.filter((p) => p.activation_status === "ACTIF").length,
  };

  const canDelete = detail && detail.commune_count === 0 && detail.status === "disponible" && !detail.partner_id;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;

  return (
    <div data-testid="admin-territories">
      <div data-testid="territory-national-stats" className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          ["total", "Territoires (France)"], ["non_couvert", "Non couverts"], ["a_reaffecter", "À réaffecter"],
          ["disponible", "Disponibles"], ["attribue", "Attribués"], ["partenaires_actifs", "Partenaires actifs"],
        ].map(([k, label]) => (
          <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4">
            <p data-testid={`territory-stat-${k}`} className="font-display text-2xl font-medium text-navy">{stats[k]}</p>
            <p className="text-xs text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {["Tous", ...STATUS_ORDER].map((f) => (
            <button
              key={f}
              data-testid={`territory-filter-${f}`}
              onClick={() => setStatusFilter(f)}
              className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${
                statusFilter === f ? "border-navy bg-navy text-white" : "border-slate-200 text-slate-500 hover:border-navy"
              }`}
            >
              {f === "Tous" ? "Tous" : STATUS_LABELS_MAP[f]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-slate-200 p-0.5">
            <button data-testid="territory-view-liste" onClick={() => setView("liste")}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${view === "liste" ? "bg-navy text-white" : "text-slate-500"}`}>
              <ListIcon className="h-3.5 w-3.5" /> Liste
            </button>
            <button data-testid="territory-view-carte" onClick={() => setView("carte")}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${view === "carte" ? "bg-navy text-white" : "text-slate-500"}`}>
              <MapIcon className="h-3.5 w-3.5" /> Carte
            </button>
          </div>
          <button
            data-testid="territory-create-button"
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cognac"
          >
            <Plus className="h-3.5 w-3.5" /> Nouveau territoire
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="territory-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Code postal, commune, code/nom territoire, partenaire…"
            className="w-72 rounded-full border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs text-navy focus:border-cognac focus:outline-none"
          />
        </div>
        <select data-testid="territory-region-filter" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-navy focus:border-cognac focus:outline-none">
          <option value="">Toutes régions</option>
          {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="territory-department-filter" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-navy focus:border-cognac focus:outline-none">
          <option value="">Tous départements</option>
          {departmentOptions.map((d) => <option key={d.code} value={d.code}>{d.code} · {d.name}</option>)}
        </select>
        <select data-testid="territory-partner-filter" value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-navy focus:border-cognac focus:outline-none">
          <option value="">Tous partenaires</option>
          <option value="__none__">Sans partenaire</option>
          {partners.map((p) => <option key={p.id} value={p.id}>{p.firstname} {p.lastname}</option>)}
        </select>
      </div>

      {view === "carte" ? (
        <div className="mt-5">
          <AdminTerritoriesMap territories={filtered} onOpenTerritory={openDetail} />
        </div>
      ) : (
        <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-4">Code</th>
                  <th className="px-5 py-4">Territoire</th>
                  <th className="px-5 py-4">Département</th>
                  <th className="px-5 py-4">Statut</th>
                  <th className="px-5 py-4">Partenaire</th>
                  <th className="px-5 py-4">Prospects</th>
                  <th className="px-5 py-4">En attente</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const derived = deriveStatus(t);
                  return (
                    <tr key={t.id} data-testid={`territory-row-${t.code}`} onClick={() => openDetail(t)}
                      className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-cream">
                      <td className="px-5 py-4 font-mono text-xs text-slate-500">{t.code}</td>
                      <td className="px-5 py-4 font-bold text-navy">{t.name}</td>
                      <td className="px-5 py-4 text-slate-500">{t.department}</td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${DERIVED_STATUS_CLS[derived]}`}>{STATUS_LABELS_MAP[derived]}</span>
                      </td>
                      <td className="px-5 py-4 text-slate-600">{t.partner ? `${t.partner.firstname} ${t.partner.lastname}` : "—"}</td>
                      <td className="px-5 py-4 text-slate-600">{t.prospect_count}</td>
                      <td className="px-5 py-4">
                        {t.pending_uncovered_count > 0 ? (
                          <span data-testid={`territory-pending-badge-${t.code}`} className="flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-600">
                            <AlertTriangle className="h-3 w-3" /> {t.pending_uncovered_count}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-400">Aucun territoire ne correspond à cette recherche.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AnimatePresence>
        {createOpen && (
          <CreateTerritoryModal
            onClose={() => setCreateOpen(false)}
            onCreated={() => { setCreateOpen(false); fetchTerritories(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {detail && (
          <AdminModalOverlay testId="territory-detail-modal" onClose={() => setDetail(null)}>
            <AdminModalCard>
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-7 py-6 sm:px-9">
                <div className="flex-1">
                  {renaming ? (
                    <div className="space-y-2">
                      <input data-testid="territory-rename-name" value={renameName} onChange={(e) => setRenameName(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-lg font-medium text-navy focus:border-cognac focus:outline-none" />
                      <input data-testid="territory-rename-code" value={renameCode} onChange={(e) => setRenameCode(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-mono text-slate-500 focus:border-cognac focus:outline-none" />
                      <div className="flex gap-2">
                        <button data-testid="territory-rename-save" disabled={busy} onClick={saveRename}
                          className="rounded-full bg-navy px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">Enregistrer</button>
                        <button data-testid="territory-rename-cancel" onClick={() => setRenaming(false)}
                          className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-500 transition-colors hover:border-navy">Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h2 className="font-display text-2xl font-medium text-navy">{detail.name}</h2>
                      <p className="mt-1 text-xs text-slate-400">{detail.code} · {detail.department} · {detail.region}</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!renaming && (
                    <button onClick={startRename} data-testid="territory-rename-open"
                      className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-navy hover:text-white" aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  <button onClick={() => setDetail(null)} data-testid="territory-detail-close"
                    className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-navy hover:text-white" aria-label="Fermer">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <AdminModalBody>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-xl border border-slate-200 p-3.5"><Building2 className="h-4 w-4 text-cognac" /><p className="mt-2 font-bold text-navy">{detail.commune_count}</p><p className="text-xs text-slate-400">Communes</p></div>
                <div className="rounded-xl border border-slate-200 p-3.5"><Users className="h-4 w-4 text-cognac" /><p className="mt-2 font-bold text-navy">{detail.prospect_count}</p><p className="text-xs text-slate-400">Prospects</p></div>
                <div className="rounded-xl border border-slate-200 p-3.5"><AlertTriangle className="h-4 w-4 text-cognac" /><p className="mt-2 font-bold text-navy">{detail.pending_uncovered_count || 0}</p><p className="text-xs text-slate-400">En attente</p></div>
              </div>

              <p className="mt-4 text-sm">
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${DERIVED_STATUS_CLS[deriveStatus(detail)]}`}>{STATUS_LABELS_MAP[deriveStatus(detail)]}</span>
              </p>

              {detail.partner && (
                <p className="mt-3 text-sm text-slate-600">Partenaire titulaire : <span className="font-bold text-navy">{detail.partner.firstname} {detail.partner.lastname}</span></p>
              )}

              {detail.neighbors?.length > 0 && (
                <div className="mt-4" data-testid="territory-neighbors-list">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Territoires limitrophes</p>
                  <div className="mt-2 space-y-1.5">
                    {detail.neighbors.map((n) => {
                      const full = territories.find((t) => t.id === n.id);
                      const nDerived = full ? deriveStatus(full) : "disponible";
                      const partnerName = full?.partner ? `${full.partner.firstname} ${full.partner.lastname}` : "Aucun partenaire";
                      return (
                        <div key={n.id} data-testid={`territory-neighbor-${n.code}`}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-2 text-xs">
                          <span className="flex items-center gap-1.5 font-semibold text-navy">
                            <MapPin className="h-3 w-3 text-cognac" /> {n.name}
                          </span>
                          <span className={`rounded-full px-2.5 py-0.5 font-bold ${DERIVED_STATUS_CLS[nDerived]}`}>{STATUS_LABELS_MAP[nDerived]}</span>
                          <span className="text-right text-slate-500">{partnerName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {detail.status === "disponible" && (
                <TerritoryUncoveredProspectsPanel territoryId={detail.id} testPrefix="territory-uncovered-prospects" />
              )}

              <TerritoryDossiersPanel territoryId={detail.id} testPrefix="territory-dossiers" />
              <TerritoryHistoryPanel history={detail.history} partners={partners} testPrefix="territory-history" />

              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Attribuer / changer de partenaire</p>
                <select
                  data-testid="territory-partner-select"
                  value={selectedPartner}
                  onChange={(e) => setSelectedPartner(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-navy focus:border-cognac focus:outline-none"
                >
                  <option value="">— Sélectionner un partenaire —</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>{p.firstname} {p.lastname} {p.company_name ? `(${p.company_name})` : ""}</option>
                  ))}
                </select>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button data-testid="territory-action-attribuer" disabled={busy} onClick={() => runAction("attribuer")}
                  className="rounded-full bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">Attribuer</button>
                <button data-testid="territory-action-changer" disabled={busy} onClick={() => runAction("changer_partenaire")}
                  className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-navy transition-colors hover:border-cognac disabled:opacity-50">Changer de partenaire</button>
                <button data-testid="territory-action-reserver" disabled={busy} onClick={() => runAction("reserver")}
                  className="rounded-full bg-amber-50 px-4 py-2 text-xs font-bold text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50">Réserver</button>
                <button data-testid="territory-action-liberer" disabled={busy} onClick={() => runAction("liberer")}
                  className="rounded-full bg-sage/20 px-4 py-2 text-xs font-bold text-navy transition-colors hover:bg-sage/30 disabled:opacity-50">Libérer</button>
                <button data-testid="territory-action-suspendre" disabled={busy} onClick={() => runAction("suspendre")}
                  className="rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-500 transition-colors hover:bg-red-100 disabled:opacity-50">Suspendre</button>
              </div>

              <TerritoryCommunesPanel territoryId={detail.id} testPrefix="territory-communes" />
              </AdminModalBody>

              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 px-7 py-5 sm:px-9">
                <button
                  data-testid="territory-detail-close-footer"
                  onClick={() => setDetail(null)}
                  className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:border-navy hover:text-navy"
                >
                  Fermer
                </button>
                {canDelete && (
                  <button data-testid="territory-delete-button" disabled={busy} onClick={deleteTerritory}
                    className="flex items-center gap-1.5 rounded-full border border-red-200 px-5 py-2.5 text-sm font-bold text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50">
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer ce territoire vide
                  </button>
                )}
              </div>
            </AdminModalCard>
          </AdminModalOverlay>
        )}
      </AnimatePresence>
    </div>
  );
};
